import { useState, useEffect } from 'react';
import { MapPin, Plus, Edit, Trash2, Star, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Switch } from './ui/switch';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';
import { devError } from '../lib/devLog';

const DEFAULT_ZONES = [
  { name: 'Hub', max_distance: 5, addon: 0.0 },
  { name: 'Zone 1', max_distance: 10, addon: 2.0 },
  { name: 'Zone 2', max_distance: 15, addon: 4.0 },
  { name: 'Zone 3', max_distance: 25, addon: 11.0 },
  { name: 'Zone 4', max_distance: 35, addon: 15.0 },
  { name: 'Zone 5', max_distance: 45, addon: 25.0 },
];

const DEFAULT_BASE_PRICES = {
  ride: 15.0,
  food: 8.0,
  grocery: 10.0,
  pharmacy: 7.0,
  package: 12.0,
};

const PRICE_LABELS = {
  ride: 'Standard Ride',
  food: 'Food Delivery',
  grocery: 'Grocery Delivery',
  pharmacy: 'Pharmacy Delivery',
  package: 'Package Delivery',
};

const emptyHub = () => ({
  name: '',
  lat: '',
  lon: '',
  state: 'IL',
  zip: '',
  is_primary: false,
  zones: DEFAULT_ZONES.map(z => ({ ...z })),
  base_prices: { ...DEFAULT_BASE_PRICES },
});

const HubsManagement = () => {
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyHub());
  const [saving, setSaving] = useState(false);

  const fetchHubs = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.listHubs();
      setHubs(res.data || []);
    } catch (e) {
      devError('Failed to load hubs', e);
      toast.error('Failed to load hubs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHubs(); }, []);

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyHub());
    setShowDialog(true);
  };

  const openEdit = (hub) => {
    setEditing(hub);
    setFormData({
      name: hub.name || '',
      lat: hub.lat ?? '',
      lon: hub.lon ?? '',
      state: hub.state || 'IL',
      zip: hub.zip || '',
      is_primary: !!hub.is_primary,
      zones: (hub.zones || DEFAULT_ZONES).map(z => ({
        name: z.name,
        max_distance: z.max_distance ?? z.radius_miles ?? 0,
        addon: z.addon ?? 0,
      })),
      base_prices: { ...DEFAULT_BASE_PRICES, ...(hub.base_prices || {}) },
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error('Hub name required'); return; }
    const lat = parseFloat(formData.lat);
    const lon = parseFloat(formData.lon);
    if (Number.isNaN(lat) || Number.isNaN(lon)) { toast.error('Valid latitude/longitude required'); return; }

    const payload = {
      name: formData.name.trim(),
      lat, lon,
      state: formData.state || 'IL',
      zip: formData.zip || '',
      is_primary: formData.is_primary,
      zones: formData.zones.map(z => ({
        name: z.name,
        max_distance: parseFloat(z.max_distance) || 0,
        addon: parseFloat(z.addon) || 0,
      })),
      base_prices: Object.fromEntries(
        Object.entries(formData.base_prices).map(([k, v]) => [k, parseFloat(v) || 0])
      ),
    };

    try {
      setSaving(true);
      if (editing) {
        await adminAPI.updateHub(editing.id, payload);
        toast.success(`Hub "${payload.name}" updated`);
      } else {
        await adminAPI.createHub(payload);
        toast.success(`Hub "${payload.name}" created`);
      }
      setShowDialog(false);
      fetchHubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save hub');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (hub) => {
    if (!confirm(`Delete hub "${hub.name}"? This cannot be undone.`)) return;
    try {
      await adminAPI.deleteHub(hub.id);
      toast.success(`Hub "${hub.name}" deleted`);
      fetchHubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete hub');
    }
  };

  const updateZone = (idx, field, value) => {
    const next = [...formData.zones];
    next[idx] = { ...next[idx], [field]: value };
    setFormData({ ...formData, zones: next });
  };

  const updatePrice = (key, value) => {
    setFormData({ ...formData, base_prices: { ...formData.base_prices, [key]: value } });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="hubs-management">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-semibold text-zinc-900">Service Hubs ({hubs.length})</h3>
          <p className="text-xs text-zinc-500">Add new hubs to expand service coverage. Each hub has its own pricing and zones.</p>
        </div>
        <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="add-hub-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Hub
        </Button>
      </div>

      {hubs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
          <MapPin className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
          <p className="text-zinc-600">No hubs configured. Add your first hub.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {hubs.map(hub => (
            <div key={hub.id} className="bg-white border border-zinc-200 rounded-xl p-4 hover:shadow-md transition" data-testid={`hub-card-${hub.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1">
                      <p className="font-semibold text-zinc-900">{hub.name}</p>
                      {hub.is_primary && (
                        <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" aria-label="Primary hub" />
                      )}
                    </div>
                    <p className="text-xs text-zinc-500">{hub.state} {hub.zip}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${hub.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-zinc-100 text-zinc-600'}`}>
                  {hub.status || 'active'}
                </span>
              </div>
              <div className="text-xs text-zinc-600 mt-2 space-y-1">
                <p>Coords: {hub.lat?.toFixed(4)}, {hub.lon?.toFixed(4)}</p>
                <p>Zones: {(hub.zones || []).length} · Max radius: {(hub.zones || []).slice(-1)[0]?.max_distance || 0} mi</p>
                <p>Ride base: ${(hub.base_prices?.ride ?? 0).toFixed(2)}</p>
              </div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-100">
                <Button size="sm" variant="outline" className="flex-1 h-8" onClick={() => openEdit(hub)} data-testid={`edit-hub-${hub.id}`}>
                  <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-red-600 hover:bg-red-50 border-red-200" onClick={() => handleDelete(hub)} data-testid={`delete-hub-${hub.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit Hub: ${editing.name}` : 'Add New Hub'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update hub coordinates, zones, and pricing.' : 'Define a new service hub. Defaults are pre-filled; tweak as needed.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Name *</Label>
                <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., O'Fallon" data-testid="hub-name-input" />
              </div>
              <div>
                <Label>State</Label>
                <Input value={formData.state} onChange={e => setFormData({ ...formData, state: e.target.value })} placeholder="IL" />
              </div>
              <div>
                <Label>Latitude *</Label>
                <Input type="number" step="0.0001" value={formData.lat} onChange={e => setFormData({ ...formData, lat: e.target.value })} placeholder="38.5923" data-testid="hub-lat-input" />
              </div>
              <div>
                <Label>Longitude *</Label>
                <Input type="number" step="0.0001" value={formData.lon} onChange={e => setFormData({ ...formData, lon: e.target.value })} placeholder="-89.9112" data-testid="hub-lon-input" />
              </div>
              <div>
                <Label>ZIP</Label>
                <Input value={formData.zip} onChange={e => setFormData({ ...formData, zip: e.target.value })} placeholder="62269" />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={formData.is_primary} onCheckedChange={v => setFormData({ ...formData, is_primary: v })} id="hub-primary" />
                <Label htmlFor="hub-primary" className="cursor-pointer">Primary hub (only one)</Label>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">Base Prices ($)</Label>
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {Object.entries(PRICE_LABELS).map(([key, label]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-xs text-zinc-600 w-32 flex-shrink-0">{label}</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.base_prices[key]}
                      onChange={e => updatePrice(key, e.target.value)}
                      className="h-8 text-sm"
                      data-testid={`hub-price-${key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">Zones (max distance in miles · addon $)</Label>
              <div className="space-y-2 mt-2">
                {formData.zones.map((z, idx) => (
                  <div key={`zone-slot-${idx}`} className="grid grid-cols-12 gap-2 items-center">
                    <Input
                      className="col-span-4 h-8 text-sm"
                      value={z.name}
                      onChange={e => updateZone(idx, 'name', e.target.value)}
                      placeholder="Zone name"
                    />
                    <Input
                      className="col-span-4 h-8 text-sm"
                      type="number"
                      step="0.1"
                      value={z.max_distance}
                      onChange={e => updateZone(idx, 'max_distance', e.target.value)}
                      placeholder="Max miles"
                    />
                    <Input
                      className="col-span-4 h-8 text-sm"
                      type="number"
                      step="0.01"
                      value={z.addon}
                      onChange={e => updateZone(idx, 'addon', e.target.value)}
                      placeholder="Addon $"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={saving}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-hub-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {editing ? 'Save Changes' : 'Create Hub'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HubsManagement;
